// Mini 3D scene thumbnail — uses real device proportions and preset camera angle
// Sets camera position AND rotation from the preset so the device appears
// in the same position within the frame as in the real viewport.

import {useMemo} from 'react'
import {Canvas, useThree} from '@react-three/fiber'
import {RoundedBox} from '@react-three/drei'
import {PRESETS, scalePresetPosition} from './cameraPresets'
import {useAppTokens} from '../../theme'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'

const CAMERA_FOV = 45
const MAX_THUMB_W = 80
const MAX_THUMB_H = 80

// ── CameraSync — sets the camera position + rotation to match the preset ──
// R3F reads the <Canvas camera> prop only once at mount, so position/rotation must
// be written imperatively here to react to device / export-aspect changes.

const CameraSync = ({position, rotation}: {position: [number, number, number]; rotation: [number, number, number]}) => {
  const camera = useThree((s) => s.camera)
  camera.position.set(position[0], position[1], position[2])
  camera.rotation.set(rotation[0], rotation[1], rotation[2])
  return null
}

// ── SimplifiedDevice — scaled to real device proportions ─────────

const SimplifiedDevice = ({
  width,
  height,
  depth,
  bodyColor,
  decalColor,
}: {
  width: number
  height: number
  depth: number
  bodyColor: string
  decalColor: string
}) => {
  const cornerRadius = Math.min(width, height) * 0.07
  const screenW = width * 0.82
  const screenH = height * 0.78
  const screenY = height * 0.02
  const homeR = Math.min(width, height) * 0.055
  const homeY = -(height / 2) * 0.9
  const frontZ = depth / 2 + 0.1

  return (
    <group>
      <RoundedBox args={[width, height, depth]} radius={cornerRadius} smoothness={4}>
        <meshBasicMaterial color={bodyColor} />
      </RoundedBox>
      <mesh position={[0, screenY, frontZ]}>
        <planeGeometry args={[screenW, screenH]} />
        <meshBasicMaterial color={decalColor} />
      </mesh>
      <mesh position={[0, homeY, frontZ]}>
        <circleGeometry args={[homeR, 24]} />
        <meshBasicMaterial color={decalColor} />
      </mesh>
    </group>
  )
}

// ── Thumbnail canvas ─────────────────────────────────────────────

interface CameraPresetThumbnailProps {
  presetId: string
}

const CameraPresetThumbnail = ({presetId}: CameraPresetThumbnailProps) => {
  const tokens = useAppTokens()
  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)

  const preset = useMemo(() => PRESETS.find((p) => p.id === presetId), [presetId])
  const cameraRot = preset?.rotation ?? ([0, 0, 0] as [number, number, number])
  // Scale the preview camera to the device size — matches the applied preset, so a
  // tablet preview isn't over-zoomed compared to the real viewport.
  const cameraPos = useMemo(
    () => scalePresetPosition(preset?.position ?? [0, 0, 200], deviceWidth, deviceHeight, exportWidth, exportHeight),
    [preset, deviceWidth, deviceHeight, exportWidth, exportHeight],
  )

  // Thumbnail dimensions matching export aspect ratio
  const thumbSize = useMemo(() => {
    const aspect = exportWidth / exportHeight
    let w: number
    let h: number
    if (aspect >= 1) {
      w = MAX_THUMB_W
      h = Math.round(MAX_THUMB_W / aspect)
    } else {
      h = MAX_THUMB_H
      w = Math.round(MAX_THUMB_H * aspect)
    }
    return {width: w, height: h}
  }, [exportWidth, exportHeight])

  const bodyColor = 'rgba(255, 255, 255, 0.75)'
  const decalColor = tokens.surface.background

  return (
    <div
      style={{
        width: thumbSize.width,
        height: thumbSize.height,
        borderRadius: 6,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <Canvas
        frameloop="demand"
        dpr={[1, 2]}
        camera={{position: cameraPos, fov: CAMERA_FOV, near: 1, far: 2000}}
        gl={{antialias: true, alpha: true}}
        style={{background: 'transparent'}}
      >
        <CameraSync position={cameraPos} rotation={cameraRot} />
        <SimplifiedDevice
          width={deviceWidth}
          height={deviceHeight}
          depth={deviceDepth}
          bodyColor={bodyColor}
          decalColor={decalColor}
        />
      </Canvas>
    </div>
  )
}

export default CameraPresetThumbnail
